import type { Container, VocabularyEntry } from "./types";

/**
 * Que propriedades cada entidade aceita. É a única camada do catálogo que um
 * gerador a partir do `schemaorg-current-https.jsonld` sabe produzir — as
 * receitas e as regras do Google não saem de lá.
 *
 * Só entram entidades que o Google transforma em resultado rico: Produto e Post
 * de blog. Veículo, Serviço, Crédito e Avaliação foram retirados — o buscador
 * não dá destaque próprio a nenhum deles, e mantê-los custava vocabulário,
 * tradução e tela sem retorno na busca.
 */
export const vocabulary: Record<string, VocabularyEntry> = {
  Product: {
    label: "Produto",
    description:
      "Qualquer bem ou serviço ofertado — veículo, peça, acessório, revisão ou plano.",
    icon: "Package",
    properties: [
      "name",
      "description",
      "image",
      "sku",
      "gtin",
      "mpn",
      "productID",
      "model",
      "category",
      "keywords",
      "color",
      "material",
      "releaseDate",
      "productionDate",
      "itemCondition",
      "slogan",
      "award",
      "brand.name",
      "offers.price",
      "offers.validFrom",
      "offers.validThrough",
      "offers.seller",
      "offers.priceSpecification.price",
    ],
  },

  BlogPosting: {
    label: "Post de blog",
    description: "Publicação editorial datada e assinada.",
    icon: "Newspaper",
    properties: [
      "headline",
      "name",
      "description",
      "articleBody",
      "image",
      "datePublished",
      "dateModified",
      "articleSection",
      "keywords",
      "wordCount",
      "author.name",
      "author.url",
      "author.image",
    ],
  },

  Offer: {
    label: "Oferta",
    description: "Condição comercial de um item. Montada a partir de offers.*",
    icon: "Tag",
    nestedOnly: true,
    properties: [],
  },

  UnitPriceSpecification: {
    label: "Preço de tabela",
    description:
      "Preço anterior de uma oferta. Montada a partir de offers.priceSpecification.*",
    icon: "TicketPercent",
    nestedOnly: true,
    properties: [],
  },

  Brand: {
    label: "Marca",
    description: "Marca do produto. Montada a partir de brand.*",
    icon: "BadgeCheck",
    nestedOnly: true,
    properties: [],
  },

  Person: {
    label: "Pessoa",
    description: "Autor da publicação. Montada a partir de author.*",
    icon: "User",
    nestedOnly: true,
    properties: [],
  },
};

/**
 * Caminhos pontuados (`offers.price`) viram objeto aninhado. O container diz que
 * `@type` esse objeto recebe e que constantes acompanham qualquer emissão dele —
 * as constantes só saem quando ao menos uma propriedade do container foi
 * mapeada, para não emitir uma oferta que só tem moeda.
 *
 * A chave é o **caminho até o container**, não o nome dele: um container pode
 * viver dentro de outro (`offers.priceSpecification`), e é o prefixo acumulado
 * que o `buildNode` usa para resolver `@type` e constantes de cada nível.
 */
export const containers: Record<string, Container> = {
  offers: {
    type: "Offer",
    constants: {
      priceCurrency: "BRL",
      availability: "https://schema.org/InStock",
    },
  },
  // "De/por": o preço atual fica em `offers.price` e o anterior aqui, marcado
  // como preço de tabela. Sem `priceType` o buscador não teria como saber qual
  // dos dois é o valor cobrado.
  "offers.priceSpecification": {
    type: "UnitPriceSpecification",
    constants: {
      priceCurrency: "BRL",
      priceType: "https://schema.org/StrikethroughPrice",
    },
  },
  brand: { type: "Brand" },
  author: { type: "Person" },
};
